// Calendar dates, as strings.
//
// A product day is a calendar date, not an instant (AGENTS.md). The account time
// zone enters at exactly one server function, `civilNowIn(userTimeZone)`;
// `todayIn` is its day-only reader. The client is told which product day it is
// looking at and never works it out. The browser-local quote stays in
// `time-zone.ts` and does not enter this arithmetic. So everything here is
// arithmetic on 'YYYY-MM-DD', and the one place a `Date` appears it is built and
// read in UTC, purely to get a weekday out of a calendar date. Nothing in this
// file can shift a date by a zone.
//
// Months live here too ('YYYY-MM', `addMonths`), for the same reason days do:
// they are the same kind of arithmetic on the same kind of server-named string.
// One implementation, in one place, because the negative-modulo case is right
// eleven months of the year whichever way it is written.

import type { IsoDay, IsoMonth, IsoYear } from './api/client.ts';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

interface Parts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

/** `null` rather than a thrown error: a malformed day is a server bug, and the
 *  screen says so instead of crashing on it. */
export function parseIsoDay(day: IsoDay): Parts | null {
  const m = ISO_DAY.exec(day);
  if (!m) return null;
  const parts = { year: Number(m[1] ?? ''), month: Number(m[2] ?? ''), day: Number(m[3] ?? '') };
  // Round-tripping catches '2026-02-31' and friends, which the regex cannot.
  return toIsoDay(parts) === day ? parts : null;
}

function toIsoDay({ year, month, day }: Parts): IsoDay {
  const utc = new Date(Date.UTC(year, month - 1, day));
  return utc.toISOString().slice(0, 10);
}

/** 0 = Sunday, matching the order `WEEKDAYS` is written in. */
export function weekdayIndex(day: IsoDay): number | null {
  const parts = parseIsoDay(day);
  if (!parts) return null;
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
}

/** 'Wednesday'. Uppercasing is the stylesheet's job, not this function's. */
export function weekdayName(day: IsoDay): string {
  const i = weekdayIndex(day);
  return i === null ? '' : (WEEKDAYS[i] ?? '');
}

/** 'August'. Taken from a DAY rather than from a month string, so there is one
 *  parser here and not two - a month screen has the month's first day anyway. */
export function monthName(day: IsoDay): string {
  const parts = parseIsoDay(day);
  return parts ? (MONTHS[parts.month - 1] ?? '') : '';
}

/** 'August 2026', from any day in it. The month screen heads itself with this. */
export function monthAndYear(day: IsoDay): string {
  const parts = parseIsoDay(day);
  return parts ? `${monthName(day)} ${parts.year}` : '';
}

/** '12 August' - day then month, no leading zero, as the design draws it. */
export function dayAndMonth(day: IsoDay): string {
  const parts = parseIsoDay(day);
  return parts ? `${parts.day} ${monthName(day)}` : '';
}

/** '12 Aug', for the sticky bar, where the full month does not fit. */
export function dayAndShortMonth(day: IsoDay): string {
  const parts = parseIsoDay(day);
  return parts ? `${parts.day} ${monthName(day).slice(0, 3)}` : '';
}

export function addDays(day: IsoDay, n: number): IsoDay {
  const parts = parseIsoDay(day);
  if (!parts) return day;
  const utc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + n));
  return utc.toISOString().slice(0, 10);
}

/**
 * `n` months on, or back, on a 'YYYY-MM'.
 *
 * The same category of thing as `addDays` above and it lives beside it for that
 * reason: arithmetic on a calendar value the SERVER named, with no clock and no
 * zone in it. It does not work out which month it IS - that is the time-zone
 * question, asked as the literal `'this'` and answered by `todayIn` on the
 * server - so nothing here may be used to invent a starting point.
 *
 * Integer work rather than `Date` month handling, which rolls 31 January into
 * March. The double modulo is what makes going BACK across January right: a
 * plain `% 12` gives -1 there and lands a year out, and only for one month of
 * the twelve, so it survives casual testing. The server keeps its own mirror of
 * this in `api/src/domain/month.ts` - the client may not import from `api/`, and
 * a lint rule enforces that.
 */
export function addMonths(month: IsoMonth, n: number): IsoMonth {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5)) - 1 + n;
  const shiftedYear = year + Math.floor(index / 12);
  const shiftedMonth = ((index % 12) + 12) % 12;
  return `${String(shiftedYear).padStart(4, '0')}-${String(shiftedMonth + 1).padStart(2, '0')}`;
}

/**
 * `n` years on, or back, on a 'YYYY'.
 *
 * The third of the same family, beside `addDays` and `addMonths` and for the
 * same reason: arithmetic on a calendar value the SERVER named, with no clock
 * and no zone in it. It does not work out which year it IS - that is the
 * time-zone question, asked as the literal `'this'` and answered by `todayIn` on
 * the server - so nothing here may be used to invent a starting point.
 *
 * Zero-padded, because a year that stops being four characters stops sorting and
 * stops slicing: `'999'` would compare below `'1000'` and a day built from it
 * would not be a day. The server keeps its own mirror of this in
 * `api/src/domain/year.ts`, since the client may not import from `api/`.
 */
export function addYears(year: IsoYear, n: number): IsoYear {
  return String(Number(year) + n).padStart(4, '0');
}

/**
 * 'YYYY-MM-DD' strings compare correctly as strings, which is the whole reason
 * the format was chosen - so this is a lexicographic comparison and not a date
 * one, deliberately.
 */
export function isAfter(a: IsoDay, b: IsoDay): boolean {
  return a > b;
}
